import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { useEmailDomainSuggestions } from '@atlas/core';
import { FieldHint, Input } from '@atlas/ui';

import Field from 'common/Form/Field';

import EmailDomainSuggestions from './EmailDomainSuggestions';

const EmailField = ({ className, field, onBlur, onChange, onFocus }) => {
  const {
    active,
    autocomplete,
    autofocus,
    disabled,
    errors,
    hint,
    isModifiable = true,
    label,
    name,
    placeholder,
    touched,
    value,
    warnings,
  } = field;

  const [activeField, setActiveField] = useState(active);

  const {
    blur: blurSuggestions,
    domainPart,
    focus: focusSuggestions,
    handleKeyDown,
    highlightedIndex,
    isOpen,
    localPart,
    matches,
    selectDomain,
    setHighlightedIndex,
  } = useEmailDomainSuggestions(value || '', onChange);

  const handleChange = useCallback(
    e => {
      onChange(e.target.value);
    },
    [onChange],
  );

  const error = touched && errors[0];
  const success = touched && !error;
  const warning = touched ? warnings?.[0]?.message : null;

  return (
    <Field isSignupForm options={field}>
      <div className="email-field">
        <Input
          active={activeField}
          autoComplete={autocomplete}
          autoFocus={autofocus}
          className={className}
          disabled={disabled || !isModifiable}
          error={error}
          label={label}
          name={name}
          onBlur={() => {
            setActiveField(false);
            blurSuggestions();
            onBlur();
          }}
          onChange={handleChange}
          onFocus={() => {
            setActiveField(true);
            focusSuggestions();
            onFocus();
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          success={success}
          touched={touched}
          value={value || ''}
          warning={warning}
        />
        {!disabled && isOpen && (
          <EmailDomainSuggestions
            domainPart={domainPart}
            highlightedIndex={highlightedIndex}
            localPart={localPart}
            matches={matches}
            onHighlight={setHighlightedIndex}
            onSelect={selectDomain}
          />
        )}
      </div>
      {!disabled && <FieldHint hint={hint} />}
    </Field>
  );
};

EmailField.propTypes = {
  className: PropTypes.string,
  field: PropTypes.shape({
    active: PropTypes.bool,
    autocomplete: PropTypes.string,
    autofocus: PropTypes.bool,
    disabled: PropTypes.bool,
    errors: PropTypes.arrayOf(PropTypes.object),
    hint: PropTypes.string,
    isModifiable: PropTypes.bool,
    label: PropTypes.string,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    touched: PropTypes.bool,
    value: PropTypes.string,
    warnings: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.bool])),
  }),
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
};

export default EmailField;
